import isEmpty from 'lodash/isEmpty';
import moment from 'moment';
import { EScheduledPromptTypes } from 'types/copilot';

import { buildMeetingAskAvomaAbsoluteUrl } from 'helpers/meetingAskAvomaNavigation';

import {
  CHAT_CONTEXT_TYPES,
  CHAT_CONTEXT_TYPE_NAMES,
  COPILOT_CONTEXT_ATTACHMENT_TYPE,
  COPILOT_NEW_CHAT_HEADER_TITLE,
  MESSAGE_AUTHOR_TYPES,
  MESSAGE_STATUS_OPTIONS,
  SCHEDULED_FOR_YOU,
  SCHEDULED_PROMPT_SCHEDULE_KINDS,
  SCHEDULED_PROMPT_STATE_TYPES
} from 'constants/copilot';
import { DATE_FORMATS } from 'constants/dateFormats';
import {
  BULLET_POINT_REGEX,
  DASHES_REGEX,
  DEAL_COPILOT_CITATION_REGEX,
  DOUBLE_ASTERISKS_REGEX,
  HEADER_SEPARATOR_LINE_REGEX,
  HTML_TAGS_REGEX,
  MEETING_ENGAGEMENT_REGEX,
  REMAINING_PIPES_REGEX,
  TABLE_CELL_REGEX,
  TIMESTAMP_REGEX,
  TRIPLE_PIPE_REGEX,
  USER_MARKDOWN_DASH_EQUALS_LINE_REGEX
} from 'constants/regex';

import { minutesSeconds } from './getPrettyTimeFromSeconds';

/**
 * Copilot chat APIs sometimes return `context_type` as a string; normalize so comparisons
 * to {@link CHAT_CONTEXT_TYPES} stay reliable (e.g. meeting history → CRM context).
 */
export const normalizeCopilotContextType = contextType => {
  if (contextType === null || contextType === undefined) {
    return CHAT_CONTEXT_TYPES.GLOBAL;
  }
  if (typeof contextType === 'number' && !Number.isNaN(contextType)) {
    return contextType;
  }
  if (typeof contextType === 'string') {
    const trimmed = contextType.trim();
    if (trimmed !== '') {
      const asNum = Number(trimmed);
      if (!Number.isNaN(asNum)) {
        return asNum;
      }
      switch (trimmed.toLowerCase()) {
        case 'meeting':
        case 'call':
          return CHAT_CONTEXT_TYPES.MEETING;
        case 'deal':
        case 'opportunity':
          return CHAT_CONTEXT_TYPES.DEAL;
        case 'company':
        case 'account':
          return CHAT_CONTEXT_TYPES.COMPANY;
        case 'global':
          return CHAT_CONTEXT_TYPES.GLOBAL;
        case 'scheduled':
          return CHAT_CONTEXT_TYPES.SCHEDULED;
        default:
          break;
      }
    }
  }
  return CHAT_CONTEXT_TYPES.GLOBAL;
};

/**
 * Merges in-memory thread messages with `GET /api/v1/copilot/chats/:uuid` detail `messages`.
 * The server can return before the first user turn is fully reflected, or return only
 * assistant rows, which would clobber the optimistic `uuid: null` user message from
 * `SEND_COPILOT_MESSAGE` if we naively spread `chat` over `prev`.
 */
/**
 * The REST detail endpoint never carries SSE-only progress fields
 * (`thinkingMessages`/`thinkingStartTime`/`thinkingEndTime`). If a message is
 * still active (not `finished_successfully`) and the in-memory copy already
 * has that progress recorded — e.g. after reconnecting an SSE stream that was
 * paused mid-run while the user was on another chat — carry it forward so the
 * "thinking" UI doesn't blank out until the run completes.
 */
const backfillThinkingState = (prevArr, mergedArr) => {
  const prevByUuid = new Map(
    prevArr.filter(m => m?.uuid != null).map(m => [m.uuid, m])
  );

  return mergedArr.map(m => {
    if (
      m?.uuid == null ||
      m.status === MESSAGE_STATUS_OPTIONS.FINISHED_SUCCESSFULLY ||
      (Array.isArray(m.thinkingMessages) && m.thinkingMessages.length > 0)
    ) {
      return m;
    }

    const prevMessage = prevByUuid.get(m.uuid);
    if (!prevMessage?.thinkingMessages?.length) {
      return m;
    }

    return {
      ...m,
      thinkingMessages: prevMessage.thinkingMessages,
      thinkingStartTime: prevMessage.thinkingStartTime,
      thinkingEndTime: prevMessage.thinkingEndTime
    };
  });
};

export const mergeCopilotChatDetailMessages = (prev, incoming) => {
  const prevArr = Array.isArray(prev) ? prev : [];
  const inc = Array.isArray(incoming) ? incoming : [];

  if (inc.length === 0) {
    return prevArr;
  }

  const incomingHasUser = inc.some(
    m => m?.author === MESSAGE_AUTHOR_TYPES.USER
  );
  if (!incomingHasUser) {
    const optimisticUserRows = prevArr.filter(
      m =>
        m?.author === MESSAGE_AUTHOR_TYPES.USER &&
        (m?.uuid == null || m?.uuid === '')
    );
    if (optimisticUserRows.length > 0) {
      return backfillThinkingState(prevArr, [...optimisticUserRows, ...inc]);
    }
  }

  if (incomingHasUser) {
    const optimisticWithExtras = prevArr.find(
      m =>
        m?.author === MESSAGE_AUTHOR_TYPES.USER &&
        (m?.uuid == null || m?.uuid === '') &&
        ((Array.isArray(m.copilotStandaloneContextPills) &&
          m.copilotStandaloneContextPills.length > 0) ||
          (Array.isArray(m.attachments) && m.attachments.length > 0))
    );
    if (optimisticWithExtras) {
      const optMsg = optimisticWithExtras.message || '';
      const pills = optimisticWithExtras.copilotStandaloneContextPills;
      const attachments = optimisticWithExtras.attachments;
      const firstMatchIdx = inc.findIndex(
        m =>
          m?.author === MESSAGE_AUTHOR_TYPES.USER &&
          (m?.message || '') === optMsg &&
          (!m.copilotStandaloneContextPills ||
            m.copilotStandaloneContextPills.length === 0)
      );
      if (firstMatchIdx === -1) {
        return backfillThinkingState(prevArr, inc);
      }
      return backfillThinkingState(
        prevArr,
        inc.map((m, i) =>
          i === firstMatchIdx
            ? {
                ...m,
                ...(pills?.length
                  ? { copilotStandaloneContextPills: pills }
                  : {}),
                // Only backfill attachments the server hasn't populated yet, so a
                // later refresh with real attachment rows still wins.
                ...(attachments?.length &&
                (!Array.isArray(m.attachments) || m.attachments.length === 0)
                  ? { attachments }
                  : {})
              }
            : m
        )
      );
    }
  }

  return backfillThinkingState(prevArr, inc);
};

export const getContextTypeLabel = type => {
  const t = normalizeCopilotContextType(type);
  if (t === CHAT_CONTEXT_TYPES.MEETING) {
    return CHAT_CONTEXT_TYPE_NAMES.MEETING;
  }

  if (t === CHAT_CONTEXT_TYPES.COMPANY) {
    return CHAT_CONTEXT_TYPE_NAMES.COMPANY;
  }

  if (t === CHAT_CONTEXT_TYPES.DEAL) {
    return CHAT_CONTEXT_TYPE_NAMES.DEAL;
  }

  if (t === CHAT_CONTEXT_TYPES.GLOBAL) {
    return CHAT_CONTEXT_TYPE_NAMES.GLOBAL;
  }

  return '';
};

// --- Copilot WebSocket stream `data_json.context_uuids` (see `helpers/websockets`)

const PRIMARY_CONTEXT_TYPE_TO_CONTEXT_UUIDS_KEY = {
  [CHAT_CONTEXT_TYPES.MEETING]: 'meeting_uuids',
  [CHAT_CONTEXT_TYPES.CALL]: 'meeting_uuids',
  [CHAT_CONTEXT_TYPES.DEAL]: 'deal_uuids',
  [CHAT_CONTEXT_TYPES.COMPANY]: 'account_uuids'
};

/** @-mention categories that still map to `data_json.context_uuids` (members/teams use `data_json.mentions.member_emails` / `team_uuids`). */
const MENTION_CATEGORY_KEY_TO_CONTEXT_UUIDS_KEY = {
  meetings: 'meeting_uuids',
  companies: 'account_uuids',
  deals: 'deal_uuids',
  playlists: 'playlist_uuids'
};

function pushUniqueContextAttachment(
  bucket,
  { attachment_type, uuid, label, domain, object_url }
) {
  if (!uuid || typeof uuid !== 'string') {
    return;
  }
  if (bucket.some(x => x.uuid === uuid)) {
    return;
  }
  const safeLabel =
    typeof label === 'string' && label.trim() !== '' ? label.trim() : uuid;
  const row = {
    attachment_type,
    uuid,
    label: safeLabel
  };
  if (typeof domain === 'string' && domain.trim() !== '') {
    row.domain = domain.trim();
  }
  if (typeof object_url === 'string' && object_url.trim() !== '') {
    row.object_url = object_url.trim();
  }
  bucket.push(row);
}

function pushUniqueString(bucket, value) {
  if (typeof value !== 'string') {
    return;
  }
  const trimmed = value.trim();
  if (!trimmed || bucket.includes(trimmed)) {
    return;
  }
  bucket.push(trimmed);
}

/**
 * Coerces `mentions.*` buckets to unique plain wire-id strings (no attachment rows).
 * Exported for the WebSocket send path so stale bundles cannot emit mixed arrays.
 */
function wireIdFromMentionBucketElement(el) {
  if (typeof el === 'string') {
    return el;
  }
  if (el && typeof el === 'object' && typeof el.uuid === 'string') {
    return el.uuid;
  }
  return null;
}

export function flattenCopilotMentionWireStringList(bucket) {
  const out = [];
  for (const el of bucket) {
    const wireId = wireIdFromMentionBucketElement(el);
    if (wireId) {
      pushUniqueString(out, wireId);
    }
  }
  return out;
}

/** Normalizes `data_json.mentions` before Copilot WebSocket send. */
export function sanitizeCopilotStreamMentions(mentions) {
  if (!mentions || typeof mentions !== 'object' || Array.isArray(mentions)) {
    return mentions;
  }
  const memberEmails = mentions.member_emails ?? mentions.memberEmails;
  const teamUuids = mentions.team_uuids ?? mentions.teamUuids;
  const out = { ...mentions };
  if (Array.isArray(memberEmails)) {
    out.member_emails = flattenCopilotMentionWireStringList(memberEmails);
    delete out.memberEmails;
  }
  if (Array.isArray(teamUuids)) {
    out.team_uuids = flattenCopilotMentionWireStringList(teamUuids);
    delete out.teamUuids;
  }
  return out;
}

function resolveMemberWireEmail(entry, id) {
  const fromField =
    typeof entry.email === 'string' && entry.email.trim() !== ''
      ? entry.email.trim()
      : null;
  const fromId = id.includes('@') ? id : null;
  return fromField || fromId;
}

function appendAdditionalContextMentionEntry(entry, context_uuids, mentions) {
  const id = entry?.id;
  if (typeof id !== 'string' || !id) {
    return;
  }
  const cat = entry?.categoryKey;
  const attachmentType =
    typeof entry.attachmentType === 'number'
      ? entry.attachmentType
      : COPILOT_CONTEXT_ATTACHMENT_TYPE.ATTACHED;
  const label =
    typeof entry.label === 'string' && entry.label.trim() !== ''
      ? entry.label.trim()
      : id;

  if (cat === 'members') {
    const email = resolveMemberWireEmail(entry, id);
    if (email) {
      pushUniqueString(mentions.member_emails, email);
    }
    return;
  }

  if (cat === 'teams') {
    pushUniqueString(mentions.team_uuids, id);
    return;
  }

  const wireKey = cat && MENTION_CATEGORY_KEY_TO_CONTEXT_UUIDS_KEY[cat];
  if (!wireKey) {
    return;
  }
  if (!context_uuids[wireKey]) {
    context_uuids[wireKey] = [];
  }
  const domain =
    cat === 'companies' &&
    typeof entry.companyDomain === 'string' &&
    entry.companyDomain.trim() !== ''
      ? entry.companyDomain.trim()
      : undefined;
  const object_url =
    cat === 'companies' &&
    typeof entry.crmObjectUrl === 'string' &&
    entry.crmObjectUrl.trim() !== ''
      ? entry.crmObjectUrl.trim()
      : undefined;
  pushUniqueContextAttachment(context_uuids[wireKey], {
    attachment_type: attachmentType,
    uuid: id,
    label,
    domain,
    object_url
  });
}

/**
 * Builds `data_json.context_uuids` and optional `data_json.mentions` for the Copilot
 * WebSocket send payload: context buckets from primary scope + @-pills, with members and
 * teams split out into `mentions.member_emails` (email strings) and `mentions.team_uuids`
 * (team uuid strings). `context_uuids` buckets use `{ attachment_type, uuid, label }` rows;
 * company rows may also include `domain` and `object_url` when provided (primary scope or
 * additional mentions). `attachment_type` is {@link COPILOT_CONTEXT_ATTACHMENT_TYPE.MENTIONED}
 * (0) for @ mentions and {@link COPILOT_CONTEXT_ATTACHMENT_TYPE.ATTACHED} (1) for Add-context
 * pills / primary scope.
 * @param {number | string | null | undefined} contextType
 * @param {string | null | undefined} contextUuid
 * @param {Array<{ id: string, categoryKey: string, email?: string, attachmentType?: number, label?: string, companyDomain?: string, crmObjectUrl?: string }> | null | undefined} additionalContextMentions
 * @param {string | null | undefined} primaryContextLabel
 * @param {{ companyDomain?: string, crmObjectUrl?: string } | null | undefined} [primaryContextWireExtras] — when primary scope is a company, optional domain / CRM URL for rehydrated pills.
 * @returns {{ context_uuids?: Record<string, { attachment_type: number, uuid: string, label: string, domain?: string, object_url?: string }[]>, mentions?: { member_emails: string[], team_uuids: string[] } } | undefined}
 */
export function getCopilotStreamDataJsonContextUuids(
  contextType,
  contextUuid,
  additionalContextMentions,
  primaryContextLabel,
  primaryContextWireExtras
) {
  const context_uuids = {};
  const mentions = { member_emails: [], team_uuids: [] };

  for (const entry of additionalContextMentions || []) {
    appendAdditionalContextMentionEntry(entry, context_uuids, mentions);
  }

  const normalizedContextType = normalizeCopilotContextType(contextType);
  const primaryKey =
    PRIMARY_CONTEXT_TYPE_TO_CONTEXT_UUIDS_KEY[normalizedContextType];
  if (primaryKey && contextUuid) {
    if (!context_uuids[primaryKey]) {
      context_uuids[primaryKey] = [];
    }
    const primaryLabel =
      typeof primaryContextLabel === 'string' &&
      primaryContextLabel.trim() !== ''
        ? primaryContextLabel.trim()
        : contextUuid;
    const pExtras = primaryContextWireExtras || {};
    const primaryDomain =
      primaryKey === 'account_uuids' &&
      typeof pExtras.companyDomain === 'string' &&
      pExtras.companyDomain.trim() !== ''
        ? pExtras.companyDomain.trim().toLowerCase()
        : undefined;
    const primaryObjectUrl =
      primaryKey === 'account_uuids' &&
      typeof pExtras.crmObjectUrl === 'string' &&
      pExtras.crmObjectUrl.trim() !== ''
        ? pExtras.crmObjectUrl.trim()
        : undefined;
    pushUniqueContextAttachment(context_uuids[primaryKey], {
      attachment_type: COPILOT_CONTEXT_ATTACHMENT_TYPE.ATTACHED,
      uuid: contextUuid,
      label: primaryLabel,
      domain: primaryDomain,
      object_url: primaryObjectUrl
    });
  }

  const hasContextUuids = Object.keys(context_uuids).length > 0;
  const hasMentions =
    mentions.member_emails.length > 0 || mentions.team_uuids.length > 0;
  if (!hasContextUuids && !hasMentions) {
    return undefined;
  }
  const out = {};
  if (hasContextUuids) {
    out.context_uuids = context_uuids;
  }
  if (hasMentions) {
    out.mentions = sanitizeCopilotStreamMentions({
      member_emails: mentions.member_emails,
      team_uuids: mentions.team_uuids
    });
  }
  return out;
}

/**
 * Derives scheduled prompts (active/paused) from saved prompts data.
 * Use this instead of calling getCopilotScheduledPrompts API when you already have getCopilotSavedPrompts data.
 * @param {Record<string, object>|undefined} savedPromptsData - Map of prompt uuid to prompt (from getCopilotSavedPrompts)
 * @param {number[]} [stateFilter] - Schedule states to include (default: ACTIVE, PAUSED)
 * @returns {object[]} Array of prompts that have at least one schedule in the given states
 */
const hasCopilotMeetingEndAutomationUuid = prompt =>
  Boolean(
    prompt?.automationUuid ?? prompt?.scheduledPrompts?.[0]?.automationUuid
  );

/**
 * Mirrors {@link isCopilotMeetingEndScheduledPrompt} without importing TS from this module
 * (avoids circular import: `copilotMeetingEndAutomationPause` already imports this file).
 * @param {object|undefined|null} prompt
 * @returns {boolean}
 */
const isCopilotMeetingEndSavedPromptForQuota = prompt => {
  if (hasCopilotMeetingEndAutomationUuid(prompt)) {
    return true;
  }

  const schedule = prompt?.scheduledPrompts?.[0];
  if (!schedule) {
    return false;
  }

  if (schedule.scheduleKind === SCHEDULED_PROMPT_SCHEDULE_KINDS.MEETING_END) {
    return true;
  }

  const rrule = schedule.rrule;
  return !(typeof rrule === 'string' && rrule.trim().length > 0);
};

export const getScheduledPromptsFromSavedPrompts = (
  savedPromptsData,
  stateFilter = [
    SCHEDULED_PROMPT_STATE_TYPES.ACTIVE,
    SCHEDULED_PROMPT_STATE_TYPES.PAUSED
  ]
) => {
  if (!savedPromptsData || typeof savedPromptsData !== 'object') {
    return [];
  }
  return Object.values(savedPromptsData).filter(prompt => {
    if (hasCopilotMeetingEndAutomationUuid(prompt)) {
      return true;
    }

    return prompt?.scheduledPrompts?.some(s =>
      stateFilter.includes(normalizeScheduledPromptScheduleState(s?.state))
    );
  });
};

/**
 * Normalize schedule `state` from the API for comparisons (numeric enums vs occasional string drift).
 * @param {unknown} state
 * @returns {number|undefined}
 */
export const normalizeScheduledPromptScheduleState = state => {
  if (typeof state === 'number' && Number.isFinite(state)) {
    return state;
  }
  if (typeof state === 'string') {
    const trimmed = state.trim();
    const upper = trimmed.toUpperCase();
    if (trimmed === '0' || upper === 'ARCHIVED') {
      return SCHEDULED_PROMPT_STATE_TYPES.ARCHIVED;
    }
    if (trimmed === '1' || upper === 'PAUSED') {
      return SCHEDULED_PROMPT_STATE_TYPES.PAUSED;
    }
    if (trimmed === '2' || upper === 'ACTIVE') {
      return SCHEDULED_PROMPT_STATE_TYPES.ACTIVE;
    }
  }
  return undefined;
};

/**
 * Each **ACTIVE** time-based schedule with an `rrule`, plus each **active** meeting-end
 * schedule (automation-driven, often with no RRULE), counts toward `maxCopilotScheduledPrompts`.
 * Iterates every `scheduledPrompts[]` entry, not only `[0]`. Paused/archived rows do not count.
 * @param {Record<string, object>|undefined|null} savedPromptsMap
 * @returns {number}
 */
export const countCopilotScheduledPromptQuotaUsageFromSavedPromptsMap =
  savedPromptsMap => {
    if (!savedPromptsMap || typeof savedPromptsMap !== 'object') {
      return 0;
    }
    let count = 0;
    for (const prompt of Object.values(savedPromptsMap)) {
      const schedules = prompt?.scheduledPrompts;
      if (Array.isArray(schedules)) {
        for (const entry of schedules) {
          if (entry?.rrule) {
            const st = normalizeScheduledPromptScheduleState(entry.state);
            if (st === SCHEDULED_PROMPT_STATE_TYPES.ACTIVE) {
              count += 1;
            }
          }
        }
      }

      if (isCopilotMeetingEndSavedPromptForQuota(prompt)) {
        const st = normalizeScheduledPromptScheduleState(
          prompt?.scheduledPrompts?.[0]?.state
        );
        if (
          st !== SCHEDULED_PROMPT_STATE_TYPES.PAUSED &&
          st !== SCHEDULED_PROMPT_STATE_TYPES.ARCHIVED
        ) {
          count += 1;
        }
      }
    }
    return count;
  };

/**
 * Reads `maxCopilotScheduledPrompts.limit` from subscription limits with safe numeric coercion.
 * @param {object|undefined|null} subscriptionLimits — `state.user.subscriptionLimits`
 * @returns {number}
 */
export const getCopilotScheduledPromptsLimitFromSubscription =
  subscriptionLimits => {
    const raw = subscriptionLimits?.limits?.maxCopilotScheduledPrompts?.limit;
    if (raw === undefined || raw === null) {
      return 10;
    }
    const n = Number(raw);
    return Number.isFinite(n) && n >= 0 ? n : 10;
  };

const escapeUserMarkdownDashEqualsLines = message =>
  message.replace(USER_MARKDOWN_DASH_EQUALS_LINE_REGEX, line =>
    line.replace(/[-=]/g, ch => `\\${ch}`)
  );

/**
 * Sentinel that replaces `<br>` inside GFM table rows so the table cell renderer
 * can split a cell back into separate lines. Uses U+241F (SYMBOL FOR UNIT
 * SEPARATOR), which never appears in model output.
 */
export const MARKDOWN_LINE_BREAK_SENTINEL = '␟';

/** Drops any sentinel a renderer did not consume, so the glyph never renders. */
export const stripMarkdownLineBreakSentinel = text =>
  typeof text === 'string' && text.includes(MARKDOWN_LINE_BREAK_SENTINEL)
    ? text.split(MARKDOWN_LINE_BREAK_SENTINEL).join(' ')
    : text;

const HTML_LINE_BREAK_TAG_REGEX = /<br\s*\/?>/gi;
const MARKDOWN_DELIMITER_ROW_CHARS_REGEX = /^[\s:|-]+$/;
const MARKDOWN_CODE_FENCE_LINE_REGEX = /^\s*(?:```|~~~)/;
const MARKDOWN_INDENTED_CODE_LINE_REGEX = /^(?: {4}|\t)/;
const MARKDOWN_BLANK_LINE_REGEX = /^\s*$/;
const MARKDOWN_INLINE_CODE_SPAN_REGEX = /(`+[^`]*`+)/;
const MARKDOWN_TRIPLE_PIPE_ROW_PREFIX = '|||';

/** Outer pipes are optional in GFM, so `--- | ---` is a delimiter row as well. */
const isMarkdownTableDelimiterLine = line =>
  MARKDOWN_DELIMITER_ROW_CHARS_REGEX.test(line) &&
  line.includes('|') &&
  line.includes('-');

/**
 * Indices of the lines that really are GFM table rows: a run of consecutive lines
 * containing a `|` whose second line is the delimiter row, which is where GFM
 * requires it. `|||` rows count too (see {@link TRIPLE_PIPE_REGEX}). Everything
 * else is prose, where a sentinel would have no renderer to consume it.
 */
const getMarkdownTableRowLineIndices = lines => {
  const tableRowLineIndices = new Set();
  let blockStartIndex = -1;

  const flushBlock = blockEndIndex => {
    if (blockStartIndex === -1) return;

    if (
      blockEndIndex - blockStartIndex >= 2 &&
      isMarkdownTableDelimiterLine(lines[blockStartIndex + 1])
    ) {
      for (let index = blockStartIndex; index < blockEndIndex; index += 1) {
        tableRowLineIndices.add(index);
      }
    }

    blockStartIndex = -1;
  };

  lines.forEach((line, index) => {
    if (!line.includes('|')) {
      flushBlock(index);
      return;
    }

    if (blockStartIndex === -1) blockStartIndex = index;
    if (line.trimStart().startsWith(MARKDOWN_TRIPLE_PIPE_ROW_PREFIX)) {
      tableRowLineIndices.add(index);
    }
  });
  flushBlock(lines.length);

  return tableRowLineIndices;
};

/**
 * Ask Avoma answers emit `<br>` to separate bullets inside table cells, but we
 * render Markdown without `rehype-raw`, so those tags are dropped and the bullets
 * collapse into one run-on paragraph. Table rows get the sentinel (a cell cannot
 * contain a real newline); everywhere else a newline is enough. Code keeps its
 * literal source, `<br>` included.
 */
export const normalizeHtmlLineBreakTags = message => {
  if (!message) return '';
  if (!message.toLowerCase().includes('<br')) return message;

  const lines = message.split('\n');
  const tableRowLineIndices = getMarkdownTableRowLineIndices(lines);
  let isInsideCodeFence = false;
  let isInsideIndentedCode = false;
  let wasPreviousLineBlank = true;

  return lines
    .map((line, index) => {
      if (MARKDOWN_CODE_FENCE_LINE_REGEX.test(line)) {
        isInsideCodeFence = !isInsideCodeFence;
        return line;
      }
      if (isInsideCodeFence) return line;

      const isBlankLine = MARKDOWN_BLANK_LINE_REGEX.test(line);
      const isIndentedLine = MARKDOWN_INDENTED_CODE_LINE_REGEX.test(line);

      // An indented code block only opens after a blank line, then runs while the
      // lines stay indented. Nested list items are indented too, but they follow a
      // list line rather than a blank one.
      isInsideIndentedCode = isInsideIndentedCode
        ? isIndentedLine || isBlankLine
        : isIndentedLine && wasPreviousLineBlank;
      wasPreviousLineBlank = isBlankLine;

      if (isInsideIndentedCode) return line;

      const lineBreakReplacement = tableRowLineIndices.has(index)
        ? MARKDOWN_LINE_BREAK_SENTINEL
        : '\n';

      return line
        .split(MARKDOWN_INLINE_CODE_SPAN_REGEX)
        .map(segment =>
          segment.startsWith('`')
            ? segment
            : segment.replace(HTML_LINE_BREAK_TAG_REGEX, lineBreakReplacement)
        )
        .join('');
    })
    .join('\n');
};

export const formatUserMarkdownMessage = message => {
  if (!message) return '';
  const escaped = escapeUserMarkdownDashEqualsLines(
    normalizeHtmlLineBreakTags(message)
  );
  return escaped.replace(/\n/g, '  \n');
};

export const formatMeetingChatMarkdownMessage = message =>
  message
    ? normalizeHtmlLineBreakTags(message)
        .replace(TRIPLE_PIPE_REGEX, '\n\n $1')
        .replace(/\n/g, '  \n')
    : '';

export const formatDealChatMarkdownMessage = message =>
  message
    ? normalizeHtmlLineBreakTags(message)
        .replace(DEAL_COPILOT_CITATION_REGEX, '>\n\n')
        .replace(/\n/g, '  \n')
    : '';

export const formatSessionTimestamp = timestamp => {
  const date = moment(timestamp);

  return date.isSame(moment(), 'day')
    ? `Today, ${date.format(DATE_FORMATS.SHORT_MONTH_DAY)}`
    : date.format(DATE_FORMATS.DAY_OF_WEEK_SHORT_MONTH_DAY);
};

export const convertMarkdownToPlainText = markdown =>
  // Trim leading and trailing whitespace
  markdown
    .replace(/<(?:M\d+(?:\.\d+)?(?:\+\d+)?|E\d+(?:\.\d+)?|\d+(?:\.\d+)?)>/g, '') // Remove all citation markers in a single pass

    .replace(HEADER_SEPARATOR_LINE_REGEX, '') // Remove header separator lines
    .replace(TABLE_CELL_REGEX, '$1') // Remove table cells with pipes
    .replace(REMAINING_PIPES_REGEX, '') // Remove remaining pipes

    .replace(DASHES_REGEX, '') // Remove dashes if there are 2 or more in a row
    .replace(BULLET_POINT_REGEX, '- ') // Replace bullet points with dashes
    .replace(DOUBLE_ASTERISKS_REGEX, '') // Remove double asterisks
    .trim();

export const convertTableElementToPlainText = tableElement => {
  const result = [];

  // Get all rows (including header rows)
  const allRows = tableElement.querySelectorAll('tr');

  if (allRows.length === 0) {
    return [];
  }

  // Iterate through each row (top to bottom)
  allRows.forEach((row, rowIndex) => {
    const cells = row.querySelectorAll('th, td');

    // Iterate through each cell in the row (left to right)
    cells.forEach(cell => {
      let cellContent = '';

      // Check if cell contains links
      const linkElements = cell.querySelectorAll('a');

      if (linkElements.length > 0) {
        // If there are links, we need to preserve both text and links
        // Create a temporary container to work with the cell content
        const tempContainer = document.createElement('div');

        tempContainer.innerHTML = cell.innerHTML;

        // Replace all link elements with their markdown format
        const tempLinks = tempContainer.querySelectorAll('a');

        tempLinks.forEach(linkElement => {
          const linkText = linkElement.textContent?.trim() || '';
          const linkHref = linkElement.getAttribute('href') || '';
          const markdownLink = `[${linkText}](${linkHref})`;

          // Replace the link element with its markdown representation
          linkElement.outerHTML = markdownLink;
        });

        // Get the text content after replacing links
        cellContent = tempContainer.textContent?.trim() || '';
      } else {
        // Otherwise, just get the text content
        cellContent = cell.textContent?.trim() || '';
      }

      // Check if this is a header cell (th element)
      if (cell.tagName.toLowerCase() === 'th') {
        // Wrap header text with bold markers
        result.push(`**${cellContent}**`);
      } else {
        result.push(cellContent);
      }
    });

    // Add line break after each row (except the last row)
    if (rowIndex < allRows.length - 1) {
      result.push('\n\n');
    }
  });

  return result;
};

export const formatMarkdownWithCitationsAsLinks = ({
  shouldIncludeTimestamps,
  markdown,
  meetingUuid,
  contextMap,
  copilotSessionUuid
}) => {
  let processedMarkdown = markdown;

  if (!shouldIncludeTimestamps) {
    // Trim trailing spaces before the tag
    processedMarkdown = processedMarkdown.replace(/\s+(?=<[^>]+>)/g, '');

    // Remove all citation markers in a single pass
    processedMarkdown = processedMarkdown.replace(
      /<(?:M\d+(?:\.\d+)?(?:\+\d+)?|E\d+(?:\.\d+)?|\d+(?:\.\d+)?)>/g,
      ''
    );
  }

  return processedMarkdown.replace(HTML_TAGS_REGEX, match => {
    const formatMarkdownLink = (id, url) => `[${minutesSeconds(id)}](${url})`;
    // Ask Avoma tab — transcript deep links assume the meeting page; unified history is app-wide.
    const formatTimestampLink = uuid =>
      buildMeetingAskAvomaAbsoluteUrl(uuid, copilotSessionUuid);

    const isTimestampCitation = TIMESTAMP_REGEX.test(match);
    const isMeetingEngagementCitation = MEETING_ENGAGEMENT_REGEX.test(match);

    if (isTimestampCitation && meetingUuid) {
      const timestamp = match.replace(/[<>]/g, '');
      const link = formatTimestampLink(meetingUuid);
      return formatMarkdownLink(timestamp, link);
    }

    if (isMeetingEngagementCitation && !isEmpty(contextMap)) {
      const meetingEngagementId = match.replace(/[<>]/g, '');
      const meetingEngagement = contextMap[meetingEngagementId];

      if (meetingEngagement) {
        const { uuid: meetingEngagementUuid, metadata: { timestamp } = {} } =
          meetingEngagement;
        const link = formatTimestampLink(meetingEngagementUuid);
        return formatMarkdownLink(timestamp, link);
      }
    }

    // Return the original match if it's not a citation we want to transform
    return match;
  });
};

export const truncateChatName = (text, maxLength = 100) => {
  if (text.length > maxLength) {
    // Subtract 3 to account for the ellipsis and slice the text
    return `${text.slice(0, maxLength - 3)}...`;
  }

  return text;
};

/**
 * Scheduled Copilot report runs appear in `GET /chats/` but are listed per prompt under Scheduled;
 * exclude them from the generic history "Chats" sidebar list.
 */
export const isCopilotScheduledReportChatSession = chat =>
  Boolean(chat?.scheduledPromptUuid);

/**
 * Recency timestamp (ms) used to bucket and sort the chat history sidebar. Prefers
 * `last_message_time` (camelized `lastMessageTime`) so threads surface by latest activity, not
 * creation; falls back to `created` for legacy rows that omit it.
 */
const getCopilotChatRecencyTimeMs = chat => {
  const lastMessageTime = chat?.lastMessageTime;
  if (typeof lastMessageTime === 'string' && lastMessageTime.trim() !== '') {
    const t = new Date(lastMessageTime).getTime();
    if (Number.isFinite(t)) {
      return t;
    }
  }
  const created = chat?.created;
  if (created != null && created !== '') {
    const t = new Date(created).getTime();
    if (Number.isFinite(t)) {
      return t;
    }
  }
  // No valid timestamp: bucket as oldest rather than letting NaN corrupt sort/bucketing.
  return 0;
};

export const groupChatsByDate = chats => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const currentYear = today.getFullYear();

  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  const lastWeekStart = new Date(today);
  lastWeekStart.setDate(lastWeekStart.getDate() - 7);

  const lastMonthStart = new Date(today);
  lastMonthStart.setDate(lastMonthStart.getDate() - 30);

  // First, group the chats by category
  const groupedChats = chats.reduce((acc, chat) => {
    const chatDate = new Date(getCopilotChatRecencyTimeMs(chat));
    chatDate.setHours(0, 0, 0, 0);

    let category = '';

    if (chatDate.getTime() === today.getTime()) {
      category = 'Today';
    } else if (chatDate.getTime() === yesterday.getTime()) {
      category = 'Yesterday';
    } else if (chatDate >= lastWeekStart) {
      category = 'Last 7 Days';
    } else if (chatDate >= lastMonthStart) {
      category = 'Last 30 Days';
    } else {
      // Only show year if it's different from current year
      const chatYear = chatDate.getFullYear();
      category = chatDate.toLocaleDateString('en-US', {
        month: 'long',
        ...(chatYear !== currentYear && { year: 'numeric' })
      });
    }

    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(chat);
    return acc;
  }, {});

  // Sort chats within each category by latest activity (newest first)
  Object.keys(groupedChats).forEach(category => {
    groupedChats[category].sort(
      (a, b) => getCopilotChatRecencyTimeMs(b) - getCopilotChatRecencyTimeMs(a)
    );
  });

  // Return an ordered object with categories in chronological order
  const orderedCategories = [
    'Today',
    'Yesterday',
    'Last 7 Days',
    'Last 30 Days'
  ];
  const result = {};

  // Add the fixed categories first
  orderedCategories.forEach(category => {
    if (groupedChats[category]?.length) {
      result[category] = groupedChats[category];
    }
  });

  // Add the remaining month-year categories, sorted newest to oldest
  Object.keys(groupedChats)
    .filter(category => !orderedCategories.includes(category))
    .sort((a, b) => new Date(b).getTime() - new Date(a).getTime())
    .forEach(category => {
      result[category] = groupedChats[category];
    });

  return result;
};

const pickFirstNonEmptyStringKey = (entity, keys) => {
  if (!entity || typeof entity !== 'object') {
    return null;
  }
  for (const key of keys) {
    const v = entity[key];
    if (typeof v === 'string' && v.trim() !== '') {
      return v.trim();
    }
    if (typeof v === 'number' && !Number.isNaN(v)) {
      return String(v);
    }
  }
  return null;
};

/** CRM payloads / meeting tags use varying keys for human-readable names. */
const COPILOT_CRM_DEAL_NAME_KEYS = [
  'name',
  'title',
  'opportunityName',
  'opportunity_name',
  'dealName',
  'deal_name',
  'displayName',
  'display_name'
];

const COPILOT_CRM_COMPANY_NAME_KEYS = [
  'name',
  'accountName',
  'account_name',
  'companyName',
  'company_name',
  'displayName',
  'display_name',
  'commonName',
  'legalName'
];

/**
 * @param {Record<string, unknown> | null | undefined} deal
 * @returns {string | null}
 */
export const pickCopilotCrmDealDisplayName = deal =>
  pickFirstNonEmptyStringKey(deal, COPILOT_CRM_DEAL_NAME_KEYS);

/**
 * @param {Record<string, unknown> | null | undefined} company
 * @returns {string | null}
 */
export const pickCopilotCrmCompanyDisplayName = company =>
  pickFirstNonEmptyStringKey(company, COPILOT_CRM_COMPANY_NAME_KEYS);

const FALLBACK_DEAL_DROPDOWN_LABEL = 'Deal';
const FALLBACK_COMPANY_DROPDOWN_LABEL = 'Company';

export const getChatDropdownLabel = ({
  contextType,
  meeting,
  company,
  deal
}) => {
  const t = normalizeCopilotContextType(contextType);
  if (t === CHAT_CONTEXT_TYPES.MEETING) {
    return `This ${meeting?.isCall ? 'call' : 'meeting'}`;
  }

  if (t === CHAT_CONTEXT_TYPES.COMPANY) {
    return (
      pickCopilotCrmCompanyDisplayName(company) ||
      FALLBACK_COMPANY_DROPDOWN_LABEL
    );
  }

  if (t === CHAT_CONTEXT_TYPES.DEAL) {
    return pickCopilotCrmDealDisplayName(deal) || FALLBACK_DEAL_DROPDOWN_LABEL;
  }

  return 'Anything';
};

function trimSelectedChatContextNameOrLabel(selectedChatContext) {
  const fromName =
    typeof selectedChatContext?.name === 'string'
      ? selectedChatContext.name.trim()
      : '';
  if (fromName) {
    return fromName;
  }
  const fromLabel =
    typeof selectedChatContext?.label === 'string'
      ? selectedChatContext.label.trim()
      : '';
  return fromLabel || '';
}

/**
 * Display label for the scoped chat entity on Copilot WebSocket `data_json.context_uuids` rows.
 * @param {{ contextType?: number, contextUuid?: string | null, selectedChatContext?: { name?: string, label?: string, contextUuid?: string | null }, meeting?: { uuid?: string, subject?: string, isCall?: boolean }, company?: Record<string, unknown> | null, deal?: Record<string, unknown> | null }} args
 * @returns {string | undefined}
 */
export const resolvePrimaryContextWireLabel = ({
  contextType,
  contextUuid,
  selectedChatContext,
  meeting,
  company,
  deal
}) => {
  if (!contextUuid || typeof contextUuid !== 'string') {
    return undefined;
  }
  const t = normalizeCopilotContextType(contextType);
  if (t === CHAT_CONTEXT_TYPES.GLOBAL) {
    return undefined;
  }
  if (t === CHAT_CONTEXT_TYPES.MEETING) {
    if (meeting?.uuid === contextUuid && meeting.subject?.trim()) {
      return meeting.subject.trim();
    }
    return (
      trimSelectedChatContextNameOrLabel(selectedChatContext) ||
      getChatDropdownLabel({ contextType: t, meeting, company, deal }) ||
      contextUuid
    );
  }
  if (t === CHAT_CONTEXT_TYPES.DEAL) {
    return (
      pickCopilotCrmDealDisplayName(deal) ||
      trimSelectedChatContextNameOrLabel(selectedChatContext) ||
      contextUuid
    );
  }
  if (t === CHAT_CONTEXT_TYPES.COMPANY) {
    return (
      pickCopilotCrmCompanyDisplayName(company) ||
      trimSelectedChatContextNameOrLabel(selectedChatContext) ||
      contextUuid
    );
  }
  return contextUuid;
};

const COPILOT_COMPANY_ENTITY_UUID_KEYS = [
  'uuid',
  'id',
  'accountUuid',
  'companyUuid'
];

function nestedCrmAccountFromCompanyRow(row) {
  const raw = row?.crmAccount;
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    return raw;
  }
  return undefined;
}

/**
 * When the primary chat scope is a company and CRM details are loaded, supply domain / CRM URL
 * for `context_uuids.account_uuids` so persisted messages rehydrate standalone pills.
 * @param {{ contextType?: number | string, contextUuid?: string | null, company?: Record<string, unknown> | null }} args
 * @returns {{ companyDomain?: string, crmObjectUrl?: string } | undefined}
 */
export const resolvePrimaryCompanyContextWireExtras = ({
  contextType,
  contextUuid,
  company
}) => {
  if (
    !contextUuid ||
    typeof contextUuid !== 'string' ||
    contextUuid.trim() === ''
  ) {
    return undefined;
  }
  const t = normalizeCopilotContextType(contextType);
  if (
    t !== CHAT_CONTEXT_TYPES.COMPANY ||
    !company ||
    typeof company !== 'object'
  ) {
    return undefined;
  }
  const entityUuid = pickFirstNonEmptyStringKey(
    company,
    COPILOT_COMPANY_ENTITY_UUID_KEYS
  );
  if (!entityUuid || String(entityUuid).trim() !== String(contextUuid).trim()) {
    return undefined;
  }
  const nested = nestedCrmAccountFromCompanyRow(company);
  const domainRaw =
    pickFirstNonEmptyStringKey(company, ['domain']) ||
    (nested ? pickFirstNonEmptyStringKey(nested, ['domain']) : null);
  const companyDomain = domainRaw ? domainRaw.trim().toLowerCase() : undefined;
  const crmUrlRaw =
    pickFirstNonEmptyStringKey(company, ['object_url', 'objectUrl', 'link']) ||
    (nested
      ? pickFirstNonEmptyStringKey(nested, ['objectUrl', 'object_url'])
      : null);
  const crmObjectUrl = crmUrlRaw ? crmUrlRaw.trim() : undefined;
  if (!companyDomain && !crmObjectUrl) {
    return undefined;
  }
  return {
    ...(companyDomain ? { companyDomain } : {}),
    ...(crmObjectUrl ? { crmObjectUrl } : {})
  };
};

export const getNewChatContextObj = ({
  contextUuid,
  contextType,
  meeting,
  company,
  deal
}) => {
  const normalizedType = normalizeCopilotContextType(
    contextType ?? CHAT_CONTEXT_TYPES.GLOBAL
  );
  const getChatTitle = () => {
    if (normalizedType === CHAT_CONTEXT_TYPES.MEETING) {
      return `Ask Avoma about ${meeting?.subject || 'this meeting'}`;
    }

    if (normalizedType === CHAT_CONTEXT_TYPES.COMPANY) {
      return (
        pickCopilotCrmCompanyDisplayName(company) ||
        FALLBACK_COMPANY_DROPDOWN_LABEL
      );
    }

    if (normalizedType === CHAT_CONTEXT_TYPES.DEAL) {
      return (
        pickCopilotCrmDealDisplayName(deal) || FALLBACK_DEAL_DROPDOWN_LABEL
      );
    }

    return COPILOT_NEW_CHAT_HEADER_TITLE;
  };

  return {
    uuid: null,
    name: getChatTitle(),
    contextUuid: contextUuid ?? null,
    contextType: normalizedType,
    label: getChatDropdownLabel({
      contextType: normalizedType,
      meeting,
      company,
      deal
    })
  };
};

export const formatTime = seconds => {
  if (seconds < 60) return `${seconds.toFixed(1)}s`;

  const mins = Math.floor(seconds / 60);
  const secs = (seconds % 60).toFixed(1);

  return `${mins}:${secs.padStart(4, '0')}`;
};

export const formatStepTime = ({
  timestamp,
  nextTimestamp,
  startTime,
  thinkingMessagesLength,
  isActive,
  endTime,
  capturedEndTime
}) => {
  if (!startTime) return '0.0s';

  const stepStart = new Date(timestamp).getTime();

  // For a single step that's completed, show the total elapsed time
  if (thinkingMessagesLength === 1 && !isActive) {
    const start = new Date(startTime).getTime();

    let end;

    if (endTime) {
      end = new Date(endTime).getTime();
    } else if (capturedEndTime !== null) {
      end = capturedEndTime;
    } else {
      // Fallback to step timestamp + buffer
      end = stepStart + 200;
    }
    let totalElapsed = Math.floor((end - start) / 100) / 10;
    // Ensure minimum duration of 0.1s for completed single steps
    if (totalElapsed <= 0) {
      totalElapsed = 0.1;
    }
    return formatTime(totalElapsed);
  }

  // Calculate duration from this step to the next step (or end time)
  let stepEnd;

  if (nextTimestamp) {
    stepEnd = new Date(nextTimestamp).getTime();
  } else if (endTime) {
    stepEnd = new Date(endTime).getTime();
  } else if (capturedEndTime !== null) {
    stepEnd = capturedEndTime;
  } else {
    // If no next timestamp and still active, return 0
    return formatTime(0);
  }

  let stepDuration = Math.floor((stepEnd - stepStart) / 100) / 10; // Convert to seconds with 1 decimal

  // Timestamps can arrive out of order while the stream is active. Never expose
  // an invalid negative duration while waiting for the final completion time.
  if (stepDuration < 0) {
    stepDuration = 0;
  }

  // Ensure minimum duration of 0.1s for completed steps
  if (!isActive && stepDuration <= 0) {
    stepDuration = 0.1;
  }

  return formatTime(stepDuration);
};

/**
 * Formats the RFC5545 DTSTART line shared by all scheduled RRULE strings.
 * @param {Object} params
 * @param {string} params.time - Time in 24h format (e.g., '09:00')
 * @param {string} [params.timezone] - IANA timezone (defaults to user's timezone)
 * @param {Date} [params.startDate] - Start date (defaults to today)
 * @returns {string} e.g. "DTSTART;TZID=America/Los_Angeles:20251208T151500"
 */
export const buildRRuleDtstart = ({
  time,
  timezone = Intl.DateTimeFormat().resolvedOptions().timeZone,
  startDate = new Date()
}) => {
  const [hours, minutes] = time.split(':');
  const year = startDate.getFullYear();
  const month = String(startDate.getMonth() + 1).padStart(2, '0');
  const day = String(startDate.getDate()).padStart(2, '0');
  return `DTSTART;TZID=${timezone}:${year}${month}${day}T${hours}${minutes}00`;
};

/**
 * Generates an RFC5545 compliant RRULE string with DTSTART for scheduled prompts
 * @param {Object} params
 * @param {string} params.frequency - 'daily' | 'weekly' | 'monthly' | 'weekdays'
 * @param {string} [params.dayOfWeek] - Day of week for weekly frequency (e.g., 'monday')
 * @param {string} [params.dayOfMonth] - Day of month for monthly frequency (e.g., '15')
 * @param {string} params.time - Time in 24h format (e.g., '09:00')
 * @param {string} [params.timezone] - IANA timezone (defaults to user's timezone)
 * @param {Date} [params.startDate] - Start date (defaults to today)
 * @returns {string} RFC5545 formatted string with DTSTART and RRULE
 */
export const generateScheduledPromptRRule = ({
  frequency,
  dayOfWeek,
  dayOfMonth,
  time,
  timezone = Intl.DateTimeFormat().resolvedOptions().timeZone,
  startDate = new Date()
}) => {
  const dtstart = buildRRuleDtstart({ time, timezone, startDate });

  // Build RRULE based on frequency
  let rrule = 'RRULE:';

  switch (frequency) {
    case 'daily':
      rrule += 'FREQ=DAILY';
      break;

    case 'weekly':
      rrule += `FREQ=WEEKLY;BYDAY=${dayOfWeek || 'MO'}`;
      break;

    case 'monthly':
      rrule += `FREQ=MONTHLY;BYMONTHDAY=${dayOfMonth || '1'}`;
      break;

    case 'weekdays':
      rrule += 'FREQ=WEEKLY;BYDAY=MO,TU,WE,TH,FR';
      break;

    default:
      rrule += 'FREQ=DAILY';
  }

  return `${dtstart}\n${rrule}`;
};

/**
 * Parses an RFC5545 RRULE string back into component-friendly values
 * @param {string} rruleString - The RRULE string (e.g., "DTSTART;TZID=America/Los_Angeles:20251204T090000\nRRULE:FREQ=MONTHLY;BYMONTHDAY=31")
 * @returns {Object} Parsed values for form fields
 */
export const parseScheduledPromptRRule = rruleString => {
  const result = {
    frequency: 'daily',
    dayOfWeek: 'MO',
    dayOfMonth: '1',
    time: '09:00',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
  };

  if (!rruleString) return result;

  // Parse DTSTART for time and timezone
  const dtstartMatch = rruleString.match(
    /DTSTART;TZID=([^:]+):(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})/
  );
  if (dtstartMatch) {
    const [, timezone, , , , hours, minutes] = dtstartMatch;
    result.timezone = timezone;
    result.time = `${hours}:${minutes}`;
  }

  // Parse RRULE for frequency and other params
  const rruleMatch = rruleString.match(/RRULE:(.+)/);
  if (!rruleMatch) return result;

  const rrulePart = rruleMatch[1];
  const params = {};

  // Parse RRULE params into key-value pairs
  rrulePart.split(';').forEach(param => {
    const [key, value] = param.split('=');
    params[key] = value;
  });

  // Determine frequency
  if (params.FREQ === 'DAILY') {
    result.frequency = 'daily';
  } else if (params.FREQ === 'WEEKLY') {
    // Check if it's weekdays (MO,TU,WE,TH,FR) or single day
    if (params.BYDAY === 'MO,TU,WE,TH,FR') {
      result.frequency = 'weekdays';
    } else {
      result.frequency = 'weekly';
      result.dayOfWeek = params.BYDAY || 'MO';
    }
  } else if (params.FREQ === 'MONTHLY') {
    result.frequency = 'monthly';
    result.dayOfMonth = params.BYMONTHDAY || '1';
  } else {
    // Default to daily for unknown frequency
    result.frequency = 'daily';
  }

  return result;
};

const DAY_NAMES = {
  MO: 'Monday',
  TU: 'Tuesday',
  WE: 'Wednesday',
  TH: 'Thursday',
  FR: 'Friday',
  SA: 'Saturday',
  SU: 'Sunday'
};

/**
 * Formats a 24-hour time string to 12-hour format with AM/PM
 * @param {string} time - Time in "HH:MM" format (e.g., "09:00", "14:30")
 * @returns {string} Formatted time (e.g., "9:00 AM", "2:30 PM")
 */
const formatTimeTo12Hour = time => {
  if (!time) return '';

  const [hoursStr, minutes] = time.split(':');
  const hours = parseInt(hoursStr, 10);
  const period = hours >= 12 ? 'PM' : 'AM';
  const hours12 = hours % 12 || 12;

  return `${hours12}:${minutes} ${period}`;
};

/**
 * Formats an RRULE string into a human-readable schedule description
 * @param {string} rruleString - The RRULE string to format
 * @returns {string} Human-readable schedule description (e.g., "Daily at 9:00 AM", "Weekly on Mondays at 9:00 AM")
 */
export const formatRruleToHumanReadable = rruleString => {
  if (!rruleString) return '';

  const parsed = parseScheduledPromptRRule(rruleString);
  const timeStr = formatTimeTo12Hour(parsed.time);

  switch (parsed.frequency) {
    case 'daily':
      return `Daily at ${timeStr}`;

    case 'weekly': {
      const dayName = DAY_NAMES[parsed.dayOfWeek] || 'Monday';
      return `Weekly on ${dayName}s at ${timeStr}`;
    }

    case 'weekdays':
      return `Every weekday at ${timeStr}`;

    case 'monthly': {
      const day = parseInt(parsed.dayOfMonth, 10);
      const suffix = getOrdinalSuffix(day);
      return `Monthly on the ${day}${suffix} at ${timeStr}`;
    }

    default:
      return `Daily at ${timeStr}`;
  }
};

/**
 * Helper function to get ordinal suffix for a number
 * @param {number} n - The number to get suffix for
 * @returns {string} Ordinal suffix (st, nd, rd, or th)
 */
const getOrdinalSuffix = n => {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return s[(v - 20) % 10] || s[v] || s[0];
};

/**
 * Calculates the next run date from an RRULE string
 * @param {string} rruleString - The RRULE string to calculate from
 * @returns {string} Formatted next run date (e.g., "Monday, Dec 10th at 10:00 AM")
 */
// eslint-disable-next-line sonarjs/cognitive-complexity -- RRULE branches are intentionally explicit
export const getNextRunDate = rruleString => {
  if (!rruleString) return '';

  const parsed = parseScheduledPromptRRule(rruleString);
  const [hours, minutes] = parsed.time.split(':').map(Number);
  const now = new Date();
  const nextRun = new Date();

  // Set the time
  nextRun.setHours(hours, minutes, 0, 0);

  // If the time has already passed today, start from tomorrow
  if (nextRun <= now) {
    nextRun.setDate(nextRun.getDate() + 1);
  }

  switch (parsed.frequency) {
    case 'daily':
      // Already set to next occurrence
      break;

    case 'weekly': {
      const dayMap = { SU: 0, MO: 1, TU: 2, WE: 3, TH: 4, FR: 5, SA: 6 };
      const targetDay = dayMap[parsed.dayOfWeek] ?? 1;
      const currentDay = nextRun.getDay();
      let daysUntilTarget = targetDay - currentDay;

      if (daysUntilTarget < 0) {
        daysUntilTarget += 7;
      }
      if (daysUntilTarget === 0 && nextRun <= now) {
        daysUntilTarget = 7;
      }

      nextRun.setDate(nextRun.getDate() + daysUntilTarget);
      break;
    }

    case 'weekdays': {
      // Find next weekday (Mon-Fri)
      while (
        nextRun.getDay() === 0 ||
        nextRun.getDay() === 6 ||
        nextRun <= now
      ) {
        nextRun.setDate(nextRun.getDate() + 1);
      }
      break;
    }

    case 'monthly': {
      const targetDayOfMonth = parseInt(parsed.dayOfMonth, 10);
      nextRun.setDate(targetDayOfMonth);

      // If the date has passed this month, go to next month
      if (nextRun <= now) {
        nextRun.setMonth(nextRun.getMonth() + 1);
        nextRun.setDate(targetDayOfMonth);
      }

      // Handle months with fewer days
      const maxDays = new Date(
        nextRun.getFullYear(),
        nextRun.getMonth() + 1,
        0
      ).getDate();

      if (targetDayOfMonth > maxDays) {
        nextRun.setDate(maxDays);
      }
      break;
    }

    default:
      break;
  }

  // Format the date
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const monthNames = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec'
  ];

  const dayOfWeekName = dayNames[nextRun.getDay()];
  const monthName = monthNames[nextRun.getMonth()];
  const dayOfMonth = nextRun.getDate();
  const suffix = getOrdinalSuffix(dayOfMonth);

  // Format time as 12-hour with AM/PM
  let formattedHours = nextRun.getHours();
  const ampm = formattedHours >= 12 ? 'PM' : 'AM';
  formattedHours %= 12;
  formattedHours = formattedHours || 12; // 0 should be 12
  const formattedMinutes = String(nextRun.getMinutes()).padStart(2, '0');

  return `${dayOfWeekName}, ${monthName} ${dayOfMonth}${suffix} at ${formattedHours}:${formattedMinutes} ${ampm}`;
};

/**
 * Converts a category enum value or 'ALL' string to a URL slug
 * @param {EScheduledPromptTypes | string} category - The category to convert
 * @returns {string} The URL slug (e.g., 'my', 'teammates', 'default', 'all')
 */
export const categoryToSlug = category => {
  if (category === 'ALL') {
    return 'all';
  }
  if (category === EScheduledPromptTypes.PRIVATE) {
    return 'my';
  }
  if (category === EScheduledPromptTypes.ORGANIZATION) {
    return 'teammates';
  }
  if (category === EScheduledPromptTypes.DEFAULT) {
    return 'default';
  }
  if (category === SCHEDULED_FOR_YOU) {
    return 'scheduled';
  }
  return 'all';
};

/**
 * Converts a URL slug to a category enum value or 'ALL'
 * @param {string} slug - The URL slug to convert
 * @returns {EScheduledPromptTypes | 'ALL'} The category enum value or 'ALL'
 */
export const slugToCategory = slug => {
  const normalized = (slug || '').toLowerCase();
  switch (normalized) {
    case 'my':
    case 'private': // Keep backward compatibility
      return EScheduledPromptTypes.PRIVATE;
    case 'teammates':
    case 'organization': // Keep backward compatibility
      return EScheduledPromptTypes.ORGANIZATION;
    case 'default':
      return EScheduledPromptTypes.DEFAULT;
    case 'scheduled':
      return SCHEDULED_FOR_YOU;
    case 'all':
    default:
      return 'ALL';
  }
};

// Fallback color combinations for collections
const FALLBACK_COLORS = [
  'bg-purple-50 border-purple-200',
  'bg-indigo-50 border-indigo-200',
  'bg-pink-50 border-pink-200',
  'bg-orange-50 border-orange-200',
  'bg-amber-50 border-amber-200',
  'bg-cyan-50 border-cyan-200',
  'bg-lime-50 border-lime-200',
  'bg-fuchsia-50 border-fuchsia-200',
  'bg-blue-50 border-blue-200'
];

// Map to track assigned colors to collection IDs
const collectionColorMap = new Map();

/**
 * Converts a collection icon to an emoji character
 * Handles both direct emoji strings and Unicode hex code point strings
 * @param {string | undefined} icon - The icon field which can be an emoji character or hex code (e.g., "💼" or "1f4c1")
 * @returns {string | null} The emoji character or null if invalid
 */
export const getCollectionIconEmoji = icon => {
  if (!icon) {
    return null;
  }
  // If it's a hex code point (only contains hex characters and possibly hyphens)
  if (/^[0-9a-f-]+$/i.test(icon)) {
    try {
      const codePoints = icon
        .split('-')
        .map(part => parseInt(part, 16))
        .filter(Number.isFinite);
      return codePoints.length ? String.fromCodePoint(...codePoints) : null;
    } catch (error) {
      return null;
    }
  }
  // Otherwise, assume it's already an emoji character
  return icon;
};

/**
 * Converts an emoji character to a hex code point string
 * Used for emoji picker which expects hex format
 * @param {string | undefined} icon - The icon field which can be an emoji character or hex code
 * @returns {string | null} The hex code point or null if invalid
 */
export const getCollectionIconHexCode = icon => {
  if (!icon) {
    return null;
  }
  // If it's already a hex code, return it
  if (/^[0-9a-f-]+$/i.test(icon)) {
    return icon;
  }
  // Convert emoji character (possibly multi‑codepoint) to hex code point(s)
  try {
    const codePoints = Array.from(icon).map(ch =>
      ch.codePointAt(0)?.toString(16).toLowerCase()
    );
    return codePoints.every(Boolean) ? codePoints.join('-') : null;
  } catch (error) {
    return null;
  }
};

/**
 * Generate a deterministic color class for a collection ID
 * Assigns colors in order of first appearance to avoid repetition when possible
 * @param {string} collectionId - The collection ID to generate a color for
 * @returns {string} Tailwind CSS classes for background and border colors
 */
export const getCollectionColorClass = collectionId => {
  if (!collectionId) {
    return FALLBACK_COLORS[0];
  }

  // Check if we already assigned a color to this collection
  if (collectionColorMap.has(collectionId)) {
    return collectionColorMap.get(collectionId);
  }

  // Assign the next available color based on map size
  const colorIndex = collectionColorMap.size % FALLBACK_COLORS.length;
  const color = FALLBACK_COLORS[colorIndex];

  collectionColorMap.set(collectionId, color);

  return color;
};
